import { useCallback, useEffect, useRef, useState } from 'react'

// The live header height is published as a CSS custom property so the preview
// can pin beneath it (top: calc(navbar + var)) and track the collapse animation
// frame-by-frame without a React re-render on every ResizeObserver tick.
export const HEADER_HEIGHT_VAR = '--lu-header-height'

// The app's global <header> is position:fixed, this tall, at z-index 900 (see
// index.scss). The page's own sticky header pins just below it (and below its
// z-index) so it never paints over the top nav. Exported (rather than kept
// local to index.tsx) so anything needing the header's real dock line — e.g.
// SignalSelector's scroll-close check — can compute it without reaching into
// index.tsx's DOM via a data-testid query.
export const NAVBAR_HEIGHT = '3.125rem'

export type UseCollapsingHeader = {
  // Callback ref for a zero-height sentinel at the very top of the page. Once it
  // scrolls up to the top of the viewport the header collapses (FR-62).
  sentinelRef: React.Ref<HTMLDivElement>
  // Callback ref for the sticky header. Its height is published to
  // HEADER_HEIGHT_VAR via a ResizeObserver.
  headerRef: React.Ref<HTMLDivElement>
  isCollapsed: boolean
}

// Small dead zone (px) so tiny scrolls at the threshold can't flip the state
// back and forth.
const COLLAPSE_HYSTERESIS = 10

// Floor for the collapse threshold, load-bearing only when nothing (no global
// banner) sits between the fixed app nav and this page: the sentinel then rests
// *on* the dock line, so "the sentinel reached the dock line" holds before the
// merchant has scrolled at all — the header collapsed on load and, resting
// inside the hysteresis band, could never expand again.
const MIN_SCROLL_TO_COLLAPSE = 48

// FR-62: drives the collapsing header.
//
// The sentinel/header mount only after data loads (the scene renders a loader
// first), so callback refs wire things up the moment each node attaches — not a
// mount-time effect that would miss them, nor a per-render layout effect (that
// re-measured every commit and caused an update loop). Collapse state updates on
// scroll/resize with hysteresis; the header height is published to a CSS var by
// a ResizeObserver so the preview follows the collapse animation without extra
// renders.
export function useCollapsingHeader(): UseCollapsingHeader {
  const sentinelNode = useRef<HTMLDivElement | null>(null)
  const headerNode = useRef<HTMLDivElement | null>(null)
  const resizeObs = useRef<ResizeObserver | null>(null)
  // The header docks at `top: NAVBAR_HEIGHT` (its own sticky offset) — cached
  // here rather than read via getComputedStyle on every scroll tick (this
  // listener is passive+capture and can fire at high frequency) and rather
  // than duplicated as a second constant so it can't drift from the header's
  // actual CSS. Refreshed whenever the header (re)mounts or the viewport
  // resizes; NAVBAR_HEIGHT itself is static, so nothing else can change it.
  const dockOffset = useRef(0)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const refreshDockOffset = useCallback(() => {
    const header = headerNode.current
    if (header) {
      dockOffset.current = parseFloat(getComputedStyle(header).top) || 0
    }
  }, [])

  const updateCollapsed = useCallback(() => {
    const sentinel = sentinelNode.current
    if (!sentinel) return
    const scrolled = window.scrollY
    // Where the sentinel sits with the page unscrolled. Re-derived every tick
    // rather than cached, so a global banner appearing or being dismissed
    // shifts the threshold with it and needs no wiring of its own.
    const restTop = sentinel.getBoundingClientRect().top + scrolled
    // Collapsing at a fixed `top <= 0` (the viewport edge) instead of the
    // header's own dock line would leave the header sitting docked-but-full-
    // height for however many px separate the sentinel from the viewport
    // edge — then snap small all at once well after it had already stopped
    // moving, reading as an unprompted jump rather than part of the same
    // motion as the dock.
    // Collapse once the sentinel reaches the header's own dock line — stated
    // as the scroll distance that takes, so the floor below can be applied in
    // the same units — and only re-expand once it's clearly back below it, so
    // the boundary can't jitter.
    const collapseAt = Math.max(
      restTop - dockOffset.current,
      MIN_SCROLL_TO_COLLAPSE
    )
    setIsCollapsed(prev =>
      prev
        ? scrolled > collapseAt - COLLAPSE_HYSTERESIS
        : scrolled >= collapseAt
    )
  }, [])

  const publishHeaderHeight = useCallback(() => {
    const node = headerNode.current
    if (node) {
      document.documentElement.style.setProperty(
        HEADER_HEIGHT_VAR,
        `${node.offsetHeight}px`
      )
    }
  }, [])

  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      sentinelNode.current = node
      updateCollapsed()
    },
    [updateCollapsed]
  )

  const headerRef = useCallback(
    (node: HTMLDivElement | null) => {
      headerNode.current = node
      resizeObs.current?.disconnect()
      resizeObs.current = null
      if (node && typeof ResizeObserver !== 'undefined') {
        resizeObs.current = new ResizeObserver(() => publishHeaderHeight())
        resizeObs.current.observe(node)
      }
      publishHeaderHeight()
      // The sentinel mounts first (it's the earlier sibling), so
      // sentinelRef's updateCollapsed() call above ran before the dock
      // offset could be read. Refresh it now that the header is attached,
      // then re-run the check so a reload that restores mid-page scroll
      // position still gets a correct initial value instead of waiting for
      // the first scroll/resize.
      refreshDockOffset()
      updateCollapsed()
    },
    [publishHeaderHeight, refreshDockOffset, updateCollapsed]
  )

  useEffect(() => {
    const handleResize = () => {
      refreshDockOffset()
      updateCollapsed()
    }
    window.addEventListener('scroll', updateCollapsed, {
      passive: true,
      capture: true,
    })
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('scroll', updateCollapsed, true)
      window.removeEventListener('resize', handleResize)
      resizeObs.current?.disconnect()
      document.documentElement.style.removeProperty(HEADER_HEIGHT_VAR)
    }
  }, [refreshDockOffset, updateCollapsed])

  return { sentinelRef, headerRef, isCollapsed }
}
